import React, { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { Formik } from "formik";
import { FloatingLabel, Form, Button as BSButton, Col, Row } from "react-bootstrap";
import { Button, FormSectionDivider, FormSectionHeader, SlideOutModal, SlideOutModalProps } from "../../../components";
import { PaymentGatewayType, PaymentGatewayConfiguration } from "../../../models";
import { MonerisConfigurationForm, StripeConfigurationForm } from "../../../components/Payments";

export interface PaymentSettingsModalProps extends SlideOutModalProps {
  initialConfig?: PaymentGatewayConfiguration;
  onSubmit: (values: PaymentGatewayConfiguration) => void;
}

export function PaymentSettingsModal(props: PaymentSettingsModalProps) {
  const submitRef = useRef<HTMLButtonElement>(null);
  
  const [selectedGateway, setSelectedGateway] = useState<string>(props.initialConfig?.name || "");

  useEffect(() => {
    if (props.showState.value) {
      setSelectedGateway(props.initialConfig?.name || "");
    }
  }, [props.showState.value]);

  useEffect(() => {
    if (props.initialConfig) {
      setSelectedGateway(props.initialConfig.name || "");
    }
  }, [props.initialConfig]);
  
  const existingConfig = React.useMemo(() => {
    if (!props.initialConfig) return {};
    if (typeof props.initialConfig.configuration === "string") {
      try {
        return JSON.parse(props.initialConfig.configuration);
      } catch {
        return {};
      }
    }
    return props.initialConfig.configuration || {};
  }, [props.initialConfig]);
 

  const getValidationSchema = (gateway: string) => {
    if (gateway === PaymentGatewayType.Stripe) {
      return yup.object().shape({
        name: yup.string().required("Payment gateway is required"),
        configuration: yup.object().shape({
          account: yup.string().required("Stripe account is required"),
          apiKey: yup.string().required("API key is required"),
          publicKey: yup.string().required("Public key is required"),
          currency: yup.string().required("Currency is required"),
        }),
      });
    } else if (gateway === PaymentGatewayType.Moneris) {
      return yup.object().shape({
        name: yup.string().required("Payment gateway is required"),
        configuration: yup.object().shape({
          storeId: yup.string().required("Moneris Store ID is required"),
          apiToken: yup.string().required("Moneris API Token is required"),
        }),
      });
    }
    return yup.object().shape({
      name: yup.string().required("Payment gateway is required"),
      configuration: yup.object(),
    });
  };

  const getInitialValues = (gateway: string, config: any) => {
    if (gateway === PaymentGatewayType.Stripe) {
      return {
        name: gateway,
        configuration: {
          account: config?.account || "",
          apiKey: config?.apiKey || "",
          publicKey: config?.publicKey || "",
          currency: config?.currency || "",
        },
      };
    } else if (gateway === PaymentGatewayType.Moneris) {
      return {
        name: gateway,
        configuration: {
          storeId: config?.storeId || "",
          apiToken: config?.apiToken || "",
        },
      };
    }
    return {
      name: gateway || "",
      configuration: {},
    };
  };

  return (
    <SlideOutModal
      {...props}
      title="Payment Settings"
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button onClick={() => props.showState.setter(false)} variant="cancel" className="w-100">
                Cancel
              </Button>
            </div>
            <div className="col-12 ps-2">
              <Button onClick={() => submitRef.current?.click()} variant="primary" className="w-100">
                Save
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <Formik
        enableReinitialize
        validationSchema={getValidationSchema(selectedGateway)}
        initialValues={getInitialValues(selectedGateway, existingConfig)}
        onSubmit={async (values) => {
          props.onSubmit(values);
          props.showState.setter(false);
        }}
      >
        {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => (
          <Form onSubmit={handleSubmit} noValidate className="px-2 pt-2">

            {/* Gateway selection */}
            <FormSectionHeader>Payment Gateway</FormSectionHeader>
            <Row className="mb-3">
              <Col>
                <FloatingLabel label="Payment Gateway">
                  <Form.Select
                    name="name"
                    value={values.name}
                    onChange={(e) => {
                      handleChange(e);
                      setSelectedGateway(e.target.value);
                      setFieldValue(
                        "configuration",
                        getInitialValues(e.target.value, {}).configuration
                      );
                    }}
                    isInvalid={!!errors.name && !!touched.name}
                  >
                    <option value="">Select a gateway</option>
                    <option value={PaymentGatewayType.Moneris}>Moneris</option>
                    <option value={PaymentGatewayType.Stripe}>Stripe</option>
                  </Form.Select>
                </FloatingLabel>
              </Col>
            </Row>

            {!values.name && (
              <p className="small text-body-secondary mb-0">
                Select a gateway to configure its credentials.
              </p>
            )}

            {values.name === PaymentGatewayType.Stripe && (
              <>
                <FormSectionDivider />
                <FormSectionHeader>Stripe Configuration</FormSectionHeader>
                <StripeConfigurationForm />
              </>
            )}
            {values.name === PaymentGatewayType.Moneris && (
              <>
                <FormSectionDivider />
                <FormSectionHeader>Moneris Configuration</FormSectionHeader>
                <MonerisConfigurationForm />
              </>
            )}
            <BSButton type="submit" ref={submitRef} className="d-none" />
          </Form>
        )}
      </Formik>
    </SlideOutModal>
  );
}

export default PaymentSettingsModal;
